import React from 'react'
import { Result, Button, Typography } from 'antd'

const { Paragraph, Text } = Typography

interface Props {
  children: React.ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
  errorInfo: React.ErrorInfo | null
}

/**
 * Error boundary globale.
 *
 * Intercetta qualsiasi errore di rendering in una route/pagina e mostra una
 * schermata di errore invece della pagina bianca. Offre pulsante di reload
 * rapido.
 */
export default class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false, error: null, errorInfo: null }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    this.setState({ errorInfo: info })
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] Render error:', error, info)
  }

  handleReload = () => {
    this.setState({ hasError: false, error: null, errorInfo: null })
    window.location.reload()
  }

  handleDismiss = () => {
    this.setState({ hasError: false, error: null, errorInfo: null })
  }

  render() {
    if (!this.state.hasError) return this.props.children
    return (
      <Result
        status="warning"
        title="Si è verificato un errore in questa pagina"
        subTitle="Prova a ricaricare. Se il problema persiste segnala al team tecnico."
        extra={[
          <Button type="primary" key="reload" onClick={this.handleReload}>
            Ricarica pagina
          </Button>,
          <Button key="dismiss" onClick={this.handleDismiss}>
            Chiudi messaggio
          </Button>,
        ]}
      >
        {this.state.error && (
          <Paragraph>
            <Text type="danger" code style={{ fontSize: 12, whiteSpace: 'pre-wrap', display: 'block', marginBottom: 8 }}>
              {this.state.error.name}: {this.state.error.message}
            </Text>
            {this.state.error.stack && (
              <details style={{ marginTop: 8 }}>
                <summary style={{ cursor: 'pointer', fontSize: 12, color: '#888' }}>
                  Stack trace (per debug)
                </summary>
                <Text type="secondary" code style={{ fontSize: 10, whiteSpace: 'pre-wrap', display: 'block', maxHeight: 300, overflow: 'auto' }}>
                  {this.state.error.stack}
                </Text>
              </details>
            )}
            {this.state.errorInfo?.componentStack && (
              <details style={{ marginTop: 8 }}>
                <summary style={{ cursor: 'pointer', fontSize: 12, color: '#888' }}>
                  Component stack (React tree)
                </summary>
                <Text type="secondary" code style={{ fontSize: 10, whiteSpace: 'pre-wrap', display: 'block', maxHeight: 300, overflow: 'auto' }}>
                  {this.state.errorInfo.componentStack}
                </Text>
              </details>
            )}
          </Paragraph>
        )}
      </Result>
    )
  }
}
